import React from 'react';

import { ControlType } from 'src/models/Sidekick/SidekickModuleSpec';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';

export const useAiToRephraseOverviewLink = (
  <ExternalLink label="Help Docs" to="https://help.gladly.com/docs/analyze-message" />
);

export const ActionsSpecs = {
  lookUpSubscription: {
    presentableDescription: (
      <span>
        Look up the subscription using the Customer’s email. The Conversation will be
        <br /> transferred to an Agent if neither the email is present nor associated subscriptions are found. This
        Action creates the{' '}
        <ExternalLink label="subscription" to="https://help.gladly.com/docs/memory-glossary#subscription" /> and{' '}
        <ExternalLink label="subscriptions" to="https://help.gladly.com/docs/memory-glossary#subscriptions" /> Memories.
      </span>
    ),
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Search greeting, signature, and reply chain',
      description: 'Look for the subscription’s email in the response’s greeting, signature, and reply chain.',
    },
    'controlCategories.0.controlGroups.0.controls.1': {
      prompt: 'Transfer Conversation',
      description: 'Transfer the Conversation to an Agent when the subscription is not found.',
    },
  },
  question: {
    presentableDescription:
      'Ask a question and gather the Customer response by configuring the Extract Memory and Send Buttons Sub-Actions.',
    'controlCategories.0.controlGroups.0.controls.0': {
      placeholder: 'How can I help you today?',
    },
    'controlCategories.1.controlGroups.0.controls.0': {
      type: ControlType.AI_REPHRASE,
      placeholder: 'Respond with a friendly and concise tone',
    },
    'controlCategories.1.controlGroups.0.controls.1': {
      hidden: true,
    },
    'controlCategories.1.controlGroups.0.controls.2': {
      prompt: 'Always ask the question',
      description: 'Ask the question even if the Memory already contains the expected answer.',
    },
  },
  sendReply: {
    presentableDescription: 'Respond to the Customer message.',
    'controlCategories.0.controlGroups.0.controls.0': {
      placeholder: 'How can I help you today?',
    },
    'controlCategories.1.controlGroups.0.controls.0': {
      type: ControlType.AI_REPHRASE,
      placeholder: 'Respond with a friendly and concise tone',
    },
    'controlCategories.1.controlGroups.0.controls.1': {
      hidden: true,
    },
  },
  cancelSubscription: {
    presentableDescription: (
      <span>
        Cancel the subscription saved in the{' '}
        <ExternalLink
          data-aid="sidekick-subscription-help-link"
          label="subscriptions"
          to="https://help.gladly.com/docs/memory-glossary#subscriptions"
        />{' '}
        Memory. Pending orders from prepaid subscriptions will be canceled and refunded.
      </span>
    ),
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Cancellation reason',
      description: 'Send a cancellation reason to the integration.',
      placeholder: 'Enter a reason',
    },
    'controlCategories.0.controlGroups.0.controls.1': {
      prompt: 'Cancel prepaid subscription',
    },
  },
  checkTrackingSLAs: {
    presentableDescription: (
      <span>
        Check shipment statuses against SLAs. The Conversation will be transferred to an Agent when the shipment speed
        and status values equal zero (0). This Action creates the{' '}
        <ExternalLink
          data-aid="sidekick-withinTrackingSLA-help-link"
          label="withinTrackingSLA"
          to="https://help.gladly.com/docs/memory-glossary#withingtrackingsla"
        />{' '}
        and{' '}
        <ExternalLink
          data-aid="sidekick-trackingResponse-help-link"
          label="trackingResponse"
          to="https://help.gladly.com/docs/memory-glossary#trackingresponse"
        />{' '}
        Memories.
      </span>
    ),
    isReversed: true,
    'controlCategories.0.controlGroups.0': {
      name: 'Standard shipping speed',
    },
    'controlCategories.0.controlGroups.1': {
      name: 'Expedited shipping speed',
    },
    'controlCategories.0.controlGroups.2': {
      name: 'Two-day shipping speed',
    },
    'controlCategories.0.controlGroups.3': {
      name: 'Overnight shipping speed',
    },
    'controlCategories.0.controlGroups.4': {
      name: 'Same-day shipping speed',
    },
  },
  filterData: {
    presentableDescription:
      'Filter by data, question, or rule to find the information in the Customer inquiry. This Action supports any data containing multiple entries like orders and subscriptions.',
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Filter data',
    },
    'controlCategories.0.controlGroups.0.controls.1': {
      prompt: 'Search for all possible data matches',
      description: 'When selected, this setting overrides the Filter By Question Sub-Action.',
    },
  },
  filterByRule: {
    presentableDescription: (
      <span>
        Filter the data using Rule conditions. Learn more about Rules in our{' '}
        <ExternalLink
          data-aid="sidekick-subscription-help-link"
          label="Help Docs"
          to="https://help.gladly.com/docs/create-and-edit-rules"
        />
        .
      </span>
    ),
  },
  filterByQuestion: {
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Question',
      placeholder: 'How can I help you today?',
    },
    'controlCategories.0.controlGroups.0.controls.1': {
      prompt: 'Allow smart matching',
      description: 'Use AI to match the response with the filtered data.',
    },
    presentableDescription: 'Ask a question and filter the data based on the response.',
  },
  rules: {
    presentableDescription: 'Group Rules together to align related conditional Actions and Pivots.',
  },
  rule: {
    presentableName: 'Add Rule',
    presentableDescription: (
      <span>
        Perform an Action or pivot to a new Thread using conditions. Learn more about Rules in our{' '}
        <ExternalLink
          data-aid="sidekick-subscription-help-link"
          label="Help Docs"
          to="https://help.gladly.com/docs/create-and-edit-rules"
        />
        .
      </span>
    ),
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Name',
      placeholder: 'Rule name',
    },
  },
  lookUpCustomer: {
    presentableDescription: (
      <span>
        Look up the Customer by email. The Conversation will be transferred to an Agent when the email is not present.
        This Action creates the{' '}
        <ExternalLink
          data-aid="sidekick-customer-help-link"
          label="customer"
          to="https://help.gladly.com/docs/memory-glossary#customer"
        />{' '}
        Memory.
      </span>
    ),
  },
  useOrderEmail: {
    presentableDescription: (
      <span>
        Look up the Customer by email. The Conversation will be transferred to an Agent when the email is not present.
        This Action creates the{' '}
        <ExternalLink
          data-aid="sidekick-customer-help-link"
          label="customer"
          to="https://help.gladly.com/docs/memory-glossary#customer"
        />{' '}
        Memory.
      </span>
    ),
  },
  lookUpOrder: {
    presentableDescription: (
      <span>
        Look up the order number in the message or use the email associated with the order. This Action creates the{' '}
        <ExternalLink
          data-aid="sidekick-order-help-link"
          label="order"
          to="https://help.gladly.com/docs/memory-glossary#order"
        />{' '}
        and orders{' '}
        <ExternalLink
          data-aid="sidekick-orders-help-link"
          label="orders"
          to="https://help.gladly.com/docs/memory-glossary#orders"
        />{' '}
        Memories.
      </span>
    ),
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Order number regular expression',
      description: (
        <span>
          Define the regular expression (regex) of the order number. Learn more about this setting in our{' '}
          <ExternalLink
            data-aid="sidekick-help-link"
            label="Help Docs."
            to="https://help.gladly.com/docs/work-with-regex"
          />
          .
        </span>
      ),
    },
    'controlCategories.0.controlGroups.0.controls.1': {
      prompt: 'Find the order number in the Conversation',
    },
    'controlCategories.0.controlGroups.0.controls.2': {
      prompt: 'Transfer Conversation',
      description: 'Transfer the Conversation to an Agent when the order number and email are not found.',
    },
  },
  askEmail: {
    presentableDescription: "Ask for the Customer's email when the order number is not present in the message.",
    'controlCategories.1.controlGroups.0.controls.0': {
      prompt: 'Question',
      placeholder: "What's your email?",
    },
  },
  askOrderNumber: {
    presentableDescription:
      "Ask for the order number when it's neither present in message nor found using the Customer's email.",
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Order number prefix',
      description: 'Add this prefix to the order number.',
      defaultValue: 'Enter prefix',
    },
    'controlCategories.1.controlGroups.0.controls.0': {
      prompt: 'Question',
      placeholder: "What's your email?",
    },
  },
  askOrderNumRegex: {
    presentableDescription:
      "Ask for the order number when it’s neither present in the message nor found using the Customer's email. A custom regular expression will be used to find the order number.",
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Order number regex',
      description: (
        <span>
          Define the order number’s regular expression. Learn more about this setting in our{' '}
          <ExternalLink data-aid="sidekick-subscription-help-link" label="Help Docs" to="#" />.
        </span>
      ),
      placeholder: 'Enter regex',
    },
    'controlCategories.1.controlGroups.0.controls.0': {
      prompt: 'Question',
      placeholder: "What's your order number?",
    },
  },
  askPhoneNumber: {
    presentableDescription: 'Ask for the phone number to retrieve the order.',
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Question',
      placeholder: "What's your phone number?",
    },
  },
  analyzeMessage: {
    presentableDescription: (
      <span>
        Detect sentiment and urgency, and configure topic-specific Rules by adding the Add Topics Sub-Action. This{' '}
        Action creates the <ExternalLink data-aid="sidekick-messageAnalysis-help-link" label="messageAnalysis" to="#" />{' '}
        Memory.
      </span>
    ),
  },
  analyzeTopics: {
    presentableDescription: (
      <span>
        Identify and save the Topics mentioned in the Customer message into the Action creates the{' '}
        <ExternalLink data-aid="sidekick-messageTopics-help-link" label="messageTopics" to="#" /> Memory.
      </span>
    ),
  },
  extractData: {
    presentableDescription: (
      <span>
        Select the expected answer based on the Customer response. This Sub-Action may create the{' '}
        <ExternalLink
          data-aid="sidekick-OrderNumber-help-link"
          label="OrderNumber"
          to="https://help.gladly.com/docs/memory-glossary#ordernumber"
        />{' '}
        ,
        <ExternalLink
          data-aid="sidekick-Email-help-link"
          label="Email"
          to="https://help.gladly.com/docs/memory-glossary#email"
        />{' '}
        ,
        <ExternalLink
          data-aid="sidekick-YesNo-help-link"
          label="YesNo"
          to="https://help.gladly.com/docs/memory-glossary#yesno"
        />{' '}
        ,{' '}
        <ExternalLink
          data-aid="sidekick-ButtonResponse-help-link"
          label="ButtonResponse"
          to="https://help.gladly.com/docs/memory-glossary#buttonresponse"
        />{' '}
        and{' '}
        <ExternalLink
          data-aid="sidekick-ZipCode-help-link"
          label="ZipCode"
          to="https://help.gladly.com/docs/memory-glossary#zipcode"
        />{' '}
        Memories.
      </span>
    ),
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Expected answer',
    },
    'controlCategories.0.controlGroups.0.controls.1': {
      prompt: 'Match extracted Memory with the expected answer',
      description:
        'If the extracted Memory matches the expected answer, continue the Thread. If not, transfer the Conversation to an Agent.',
    },
    'controlCategories.0.controlGroups.0.controls.2': {
      prompt: 'Search greeting, signature, and reply chain',
      description: "Look for the expected answer in the response's greeting, signature, and reply chain.",
    },
  },
  sendButtons: {
    presentableDescription:
      'Offer the Customer quick reply buttons within the chat. After setting up this Sub-Action, choose “Button response” in the Extract Memory Sub-Action.',
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Button response',
      description: (
        <span>
          Add, reorder, and assign the buttons as{' '}
          <ExternalLink
            data-aid="sidekick-SmartButtons-help-link"
            label="Smart Buttons"
            to="https://help.gladly.com/docs/buttons-overview#smart-buttons"
          />
          , reordering them dynamically based on the frequency of user interactions.
        </span>
      ),
    },
  },
  extractRegex: {
    presentableDescription: (
      <span>
        Use a regular expression to match with the Customer response. This Sub-Action creates the{' '}
        <ExternalLink
          data-aid="sidekick-Regex-help-link"
          label="Regex"
          to="https://help.gladly.com/docs/work-with-regex"
        />{' '}
        Memory.
      </span>
    ),
    'controlCategories.0.controlGroups.0.controls.1': {
      hidden: true,
    },
    'controlCategories.0.controlGroups.0.controls.2': {
      prompt: 'Match extracted Memory with the expected answer',
      description:
        'If the extracted Memory matches the expected answer, continue the Thread. If not, transfer the Conversation to an Agent.',
    },
    'controlCategories.0.controlGroups.0.controls.3': {
      prompt: 'Search greeting, signature, and reply chain',
      description: "Look for the expected answer in the response's greeting, signature, and reply chain.",
    },
  },
  generativeReply: {
    presentableDescription: (
      <span>
        Generate conversational responses to Customer inquiries using your Answers and AI. Learn about using Generative
        Reply in our <ExternalLink data-aid="sidekick-subscription-help-link" label="Help Docs" to="#" />.
      </span>
    ),
  },
  lookUpReturn: {
    presentableDescription: (
      <span>
        Look up the return by locating the order number in the message. This Action creates the{' '}
        <ExternalLink data-aid="sidekick-return-help-link" label="return" to="#" /> and{' '}
        <ExternalLink data-aid="sidekick-returns-help-link" label="returns" to="#" /> Memories. The Conversation will be{' '}
        transferred to an Agent when there is no order number present.
      </span>
    ),
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Order number regex',
      description: (
        <span>
          Define the order number’s regular expression. Learn more about this setting in our{' '}
          <ExternalLink data-aid="sidekick-subscription-help-link" label="Help Docs" to="#" />.
        </span>
      ),
      placeholder: 'Enter regex',
    },
    'controlCategories.0.controlGroups.0.controls.1': {
      prompt: 'Search greeting, signature, and reply chain',
      description: 'Look the order number in the response’s greeting, signature, and reply chain. ',
    },
  },
  lookUpTrackingInfo: {
    presentableDescription: (
      <span>
        Look up the tracking information for all shipments. This Action creates the{' '}
        <ExternalLink
          data-aid="sidekick-trackedShipments-help-link"
          label="trackedShipments"
          to="https://help.gladly.com/docs/memory-glossary#trackedshipments"
        />{' '}
        Memory.
      </span>
    ),
  },
  refundOrder: {
    presentableDescription: 'Process the refund using the selected integration, excluding restocking fee.',
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Refund shipping and handling',
      description: 'Include the full shipping and handling fee in the refund.',
    },
  },
  sendKnowledgeBaseResults: {
    presentableDescription: 'Respond to Customer inquiry using Answers.',
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Maximum number of Answers sent',
      isReversed: true,
    },
    'controlCategories.0.controlGroups.0.controls.1': {
      prompt: 'Transfer Conversation',
      description: 'Transfer the Conversation to an Agent when there are no Answers found.',
    },
    'controlCategories.1.controlGroups.0.controls.0': {
      prompt: 'Answer Reply',
      description:
        'Reply with the Answers found. Always include the {{kbResponse | newline_to_br}} filter to show the Answers in the message.',
    },
  },
  askHelpful: {
    presentableDescription: 'Ask whether the Answer was helpful.',
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Question',
      placeholder: 'Did this help?',
    },
  },
  sendProductRecommendations: {
    'controlCategories.0.controlGroups.0.controls.0': {
      isReversed: true,
    },
  },
  addTopics: {
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Topics',
      type: ControlType.ADD_TOPICS,
    },
    'controlCategories.0.controlGroups.0.controls.1': {
      type: ControlType.HIDDEN,
    },
  },
  addNote: {
    presentableDescription: 'Create an internal note visible only to your team.',
    'controlCategories.1.controlGroups.0.controls.0': {
      type: ControlType.AI_REPHRASE,
      placeholder: 'Summarize notes in bullet points',
    },
    'controlCategories.1.controlGroups.0.controls.1': {
      hidden: true,
    },
    'controlCategories.1.controlGroups.0.controls.2': {
      hidden: true,
    },
  },
  addReply: {
    presentableDescription: 'Respond to the Customer message in the same Channel.',
    'controlCategories.0.controlGroups.0.controls.0': {
      placeholder: 'How can I help you today?',
    },
    'controlCategories.1.controlGroups.0.controls.0': {
      type: ControlType.AI_REPHRASE,
      placeholder: 'Respond with a friendly and concise tone',
    },
    'controlCategories.1.controlGroups.0.controls.1': {
      hidden: true,
    },
  },
  transfer: {
    presentableDescription: 'Transfer the Conversation to an Agent.',
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Transfer reason',
      description: 'Inform the Agent the transfer reason and save the information for reporting.',
      isHtml: true,
    },
  },
  updateCustomer: {
    presentableDescription: (
      <span>
        Select the integration and use the Sub-Actions to add tags and send the activation email. This Action updates{' '}
        the{' '}
        <ExternalLink
          data-aid="sidekick-customer-help-link"
          label="customer"
          to="https://help.gladly.com/docs/memory-glossary#customer"
        />{' '}
        Memory.
      </span>
    ),
  },
  addCustomerTags: {
    presentableDescription: 'Assign tags to the customer and use them in Rules.',
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Tags',
      placeholder: 'Enter tag',
    },
  },
  updateOrder: {
    presentableDescription: (
      <span>
        Update the order by adding tags and creating reshipments for eligible orders. This Action updates the{' '}
        <ExternalLink
          data-aid="sidekick-order-help-link"
          label="order"
          to="https://help.gladly.com/docs/memory-glossary#order"
        />{' '}
        Memory.
      </span>
    ),
  },
  addOrderTags: {
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Tags',
      placeholder: 'Enter tag',
    },
  },
  createReshipment: {
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Discount name',
      description: 'Name the reshipment discount for reporting.',
      placeholder: 'Enter name',
    },
    'controlCategories.0.controlGroups.0.controls.1': {
      isReversed: true,
    },
  },
  updateShipment: {
    presentableDescription: (
      <span>
        Add the Cancel and Hold Shipment Sub-Actions to modify the shipment. This Action updates the{' '}
        <ExternalLink
          data-aid="sidekick-shipment-help-link"
          label="shipment"
          to="https://help.gladly.com/docs/memory-glossary#shipment"
        />{' '}
        Memory.
      </span>
    ),
  },
  cancelShipment: {
    presentableDescription: (
      <span>
        Cancel the shipments saved in the{' '}
        <ExternalLink
          data-aid="sidekick-shipment-help-link"
          label="shipment"
          to="https://help.gladly.com/docs/memory-glossary#shipment"
        />{' '}
        Memory.
      </span>
    ),
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Cancel all shipments',
    },
  },
  holdShipment: {
    presentableDescription: 'Place a hold on eligible shipments in the delivery process.',
    'controlCategories.0.controlGroups.0.controls.0': {
      prompt: 'Hold all shipments',
    },
    'controlCategories.0.controlGroups.0.controls.1': {
      prompt: 'Hold reason',
      description: 'Provide reason for the shipment hold.',
      placeholder: 'Enter reason',
    },
  },
  updateSubscription: {
    presentableDescription: (
      <span>
        Update the subscription by selecting the Customer and subscription integrations and adding the Pause{' '}
        Subscription Sub-Action. This Action modifies{' '}
        <ExternalLink
          data-aid="sidekick-subscription-help-link"
          label="subscription"
          to="https://help.gladly.com/docs/memory-glossary#subscription"
        />{' '}
        Memory.
      </span>
    ),
  },
  pauseSubscription: {
    presentableDescription: "Pause the Customer's subscription for one subscription term interval.",
  },
  sendCard: {
    presentableName: 'Send Card',
    presentableDescription: 'Choose a Card to display to the Customer. A Memory is required to populate the Card.',
    'controlCategories.0.controlGroups.0.controls.1': {
      prompt: 'Card',
      placeholder: 'Select a card',
    },
    'controlCategories.0.controlGroups.0.controls.2': {
      prompt: 'Memory',
    },
  },
};

const memoryNameDescriptionUrl = (
  <ExternalLink
    data-aid="sidekick-subscription-help-link"
    label="Help Docs"
    to="https://help.gladly.com/docs/memories-overview"
  />
);

export const memoryNameDescription = (
  <>Name the Memory collected from this Action. Learn about naming a Memory in our {memoryNameDescriptionUrl}. </>
);
